import { ReactElement } from "react"
import { Link, useNavigate } from "react-router-dom"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faArrowRight, faFileExcel, faCloud, IconDefinition } from "@fortawesome/free-solid-svg-icons"

import { Report, SearchResponse } from "../../../interfaces/dataInterfaces"
import { ReportStatuses } from "../../../constants/prices"
import { CONTRIBUTOR_ROLE, MARKET_MODULE } from "../../../constants/constants"

import { useAppSelector } from "../../../hooks/hooksStore"
import { selectUser } from "../../../features/common/commonSlice"

import DashboardCard from "./DashboardCard"

type Props = {
  data: SearchResponse<Report> | undefined
  isLoading: boolean
  title: string
  status: number
  havePermission?: boolean
  isEmpty?: boolean
  subtitle?: string
  icon?: IconDefinition
}

const ReportCard = ({
  data,
  isLoading,
  title,
  status,
  subtitle,
  icon,
  havePermission = true,
  isEmpty = false,
}: Props): ReactElement => {
  const navigate = useNavigate()
  const user = useAppSelector(selectUser)

  const isContributor =
    user?.roles?.find(role => role.applicationModuleId === MARKET_MODULE)?.roleName === CONTRIBUTOR_ROLE

  const getReportLink = (report: Report): string => {
    const defaultLink = ""

    switch (status) {
      case ReportStatuses.InProgress:
        return report.sourceType === "excel"
          ? `/market-prices/data-upload/${report.reportId}`
          : `/market-prices/moda-data-upload/${report.reportId}`
      case ReportStatuses.Pending:
        if (havePermission) return `/market-prices/reports/${report.reportId}#review`
        return defaultLink

      default:
        return defaultLink
    }
  }

  return (
    <DashboardCard title={title} count={data?.count ?? 0} icon={icon} subtitle={subtitle}>
      {isLoading ? (
        <div className="flex justify-center w-full p-4 flex-col gap-2">
          <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
        </div>
      ) : (
        <div>
          {isEmpty && (
            <div className="px-6 pb-4">
              <p className="text-sm text-gray-500">No reports to display</p>
            </div>
          )}
          <div className="space-y-0.5 px-6 pb-4">
            {data?.items?.map(report => (
              <div
                key={report.reportId}
                className="!py-0.5 px-3 rounded-md flex items-center justify-between cursor-pointer hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100"
                onClick={() => navigate(getReportLink(report))}
                aria-hidden
                style={{ pointerEvents: havePermission ? "auto" : "none" }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="min-w-[18px]">
                      {report.sourceType === "excel" ? (
                        <FontAwesomeIcon icon={faFileExcel} size="sm" color="green" title="Excel" />
                      ) : (
                        <FontAwesomeIcon icon={faCloud} size="sm" color="var(--interactive-01)" title="MoDA" />
                      )}
                    </div>
                    <p className="text-sm text-gray-900">#{report.reportId}</p>
                    {!isContributor && <p className="text-sm text-gray-900">{report.countryName}</p>}
                    <p className="text-xs text-gray-500">
                      {report.dateRange?.start ? (
                        <span>
                          {new Date(report.dateRange?.start).toLocaleDateString("en-GB", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          -{" "}
                        </span>
                      ) : (
                        ""
                      )}
                      {report.dateRange?.end
                        ? new Date(report.dateRange?.end).toLocaleDateString("en-GB", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : ""}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {report.isUpdated && (
                    <div className="relative flex w-2.5 h-2.5 ml-1" title="New Data Fetched">
                      <div className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                      <div className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#41B300] animate-pulse" />
                    </div>
                  )}

                  {havePermission && <FontAwesomeIcon icon={faChevronRight} size="sm" className="text-gray-400" />}
                </div>
              </div>
            ))}
          </div>

          {!isEmpty && (
            <div className="border-t border-gray-200 p-2 bg-gray-50 transition-colors">
              <Link
                to={`/market-prices/reports?status=${status}`}
                className="flex items-center justify-center gap-2 text-sm font-semibold text-[var(--interactive-01)] hover:text-blue-800"
              >
                <span>View all {title?.toLowerCase()}</span>
                <FontAwesomeIcon icon={faArrowRight} size="sm" />
              </Link>
            </div>
          )}
        </div>
      )}
    </DashboardCard>
  )
}

export default ReportCard
